import RenderManager from "../rendering/render-manager";
import Utils from "../utils";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";
import Base2DElementDynamic from "./base-2d-element-dynamic";
import Transition from "./Transition";
import getWheelDelta from "../utils/wheel-delta";

class ImageCarousel extends Base2DElementDynamic {
  get type2d() {
    return "ImageCarousel";
  }

  get getVWSize() {
    return this._getVWSize;
  }

  _initDefaultVars() {
    super._initDefaultVars();
  }

  _initValues(options) {
    this.y = options.y;
    this.x = options.x;
    this.autoRotate = options.autoRotate || false;

    this.aspect = options.aspect || '1x1'  // 1x1, 16x9, 9x16
    this.hideBackface = options.hideBackface || false;
    this.ratio = 1;
    this.images = options.images ? options.images.map(i => ({
      action: i.action || [],
      fileName: i.fileName,
      src: i.src.replace(Q3.s3Url, ""),
      empty: Boolean(i.empty)
    })) : [
      {
        fileName: "empty1",
        src: "/images/placeholder150.png",
        empty: true,
      },
      {
        fileName: "empty2",
        src: "/images/placeholder150.png",
        empty: true,

      },
      {
        fileName: "empty3",
        src: "/images/placeholder150.png",
        empty: true,
      },
    ];

    this.sizeSm = options.sizeSm
      ? options.sizeSm
      : {
          x: 0.05,
          y: 0.05,
          width: 0.4,
          height: 0.5,
        };

    this.sizeLg = options.sizeLg
      ? options.sizeLg
      : {
          x: 0.2,
          y: 0.2875,
          width: 0.4,
          height: 0.5,
        };
  }

  get supportsActions() {
    return true;
  }
  get content() {
    return this._content;
  }
  set content(val) {
    this._content = val;
  }

  constructor(options, parentLayer, uuid = THREE.Math.generateUUID()) {
    super(options, parentLayer, uuid);

    const size = RenderManager.getSize();

    this.width = options.width ||  300;
    this.height = options.height || size.height;
    this.radius = null; // how big of the radius
    this.rotateSpeed = -60; // unit: seconds /360 degrees

    this.desX = 0.95;
    this.desY = 0.95;
    this.tX = 15;

    //dom insertion
    this.className = `css-${this.uuid}`;
    this.domElement.className = `${this.className} ${this.extraClassname}`;
    this.domElement.style.position = "absolute";
    this.domElement.style.display = "flex";
    this.domElement.style.alignItems = "center";
    this.domElement.style.justifyContent = "center";
    this.domElement.style.pointerEvents = "auto";

    this.carouselContainer = document.createElement("div");
    this.carouselContainer.className = "carousel-container";
    this.carouselContainer.style.height = "85%";

    this.spinContainer = document.createElement("div");
    this.spinContainer.className = "spin-container";
    this.imagesDom = [];

    this.carouselContainer.appendChild(this.spinContainer);

    this.ground = document.createElement("div");
    this.ground.className = "ground";

    this.domElement.appendChild(this.carouselContainer);
    this.parentLayer.domElement.appendChild(this.domElement);

    this._separateReflectionElements = !CSS.supports('-webkit-box-reflect', 'below 10px linear-gradient(transparent, transparent, #0005)');

    this.applyTranform(this.carouselContainer);
    if (!Q3.editMode) {
      // setup events
      this.initEvents();
    }

    if (Q3.isReady) {
      this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
      this.enterExitCtrl.init(options);
    } else {
      Q3.Events.listen("tourReady", () => {
        this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
        this.enterExitCtrl.init(options);
      });
    }

    if (Q3.editMode) {
      this.domElement.addEventListener(
        "click",
        (e) => {
          this.selectHandler(e);
        },
        false
      );
    } else {
      this.domElement.style.pointerEvents = this.disableEvents ? "none" : "auto";
      if (this.hiddenMobile) {
        this.domElement.classList.add("hidden-mobile");
      }
      if (this.hiddenDesktop) {
        this.domElement.classList.add("hidden-desktop");
      }
    }

    const { width, height } = RenderManager.getSize();

    this.redraw = this.redraw.bind(this);
    this.handleListeners(true);
    this.appendImages();
    this.redraw(width, height);
    this.init = this.init.bind(this);
  }

  afterUndo() {
    this.remapImages();
  }

  remapImages() {
    this.images = this.data.images.map(i => ({fileName: i.fileName, src: i.src.replace(Q3.s3Url, ""), empty: Boolean(i.empty)}))
    this.appendImages();
  }

  appendImages() {
    this.imagesDom = [];
    while (this.spinContainer.firstChild) {
      this.spinContainer.removeChild(this.spinContainer.lastChild);
    }
    for (let [i, imgData] of this.images.entries()) {
      let wrap = document.createElement("div");
      wrap.className = `img_wrap_carousel media-preloader ${imgData.isLoading ? "is-loading" : ""}`
      let image = document.createElement("img");
      image.src = (imgData.empty ? "" : Q3.s3Url) + imgData.src + (Q3.browserCapabilities.supports.WebP ? ".webp" : "");
      image.setAttribute('crossorigin', "anonymous");
      wrap.appendChild(image)
      if (this._separateReflectionElements && !Q3.browserCapabilities.isFirefox) {
        let imageId = `image-carousel_${this.uuid}_${i}`;
        image.id = imageId;
        let reflection = document.createElement("div");
        reflection.className = 'carousel-reflection';
        reflection.style.backgroundImage = `-moz-element(#${imageId})`;
        wrap.appendChild(reflection);
      }
      this.spinContainer.appendChild(wrap);
      this.imagesDom.push(wrap);
      this.initActions(imgData, wrap)
    }
    this.redraw();
  }

  initActions(imgData, wrap){
    if (!Q3.editMode && typeof imgData.action !== "undefined" && imgData.action.length) {
      wrap.style.cursor = "pointer";
      let transitions = [];
      imgData.action.forEach(currAction => {
        const transition = new Transition(currAction.systemAction, currAction.value, this);
        transitions.push(transition)
      });
      let onClick = () => {
        if (!this.drag) {
          transitions.forEach(t => t.click())
        }
      }
      wrap.addEventListener('click', onClick)
      wrap.addEventListener('touchend', onClick)
    }
  }

  applyTranform(obj) {
    // Apply the angle'
    obj.style.transform = "rotateX(-7deg) rotateY(" + this.tX + "deg)";
  }

  playSpin(yes) {
    this.spinContainer.style.animationPlayState = yes ? "running" : "paused";
  }

  initEvents() {

    this.domElement.onwheel = (e) => {
      e = e || window.event;
      let dir = getWheelDelta(e, Math.abs(e.deltaX) ? 'deltaX' : 'deltaY') / 8.75 || -e.detail;
      this.tX += dir;
      this.applyTranform(this.carouselContainer);
    };

    const onpointerdownEvent = (e) => {
      this.drag = false;
      if (typeof this.carouselContainer.timer !== "undefined") clearInterval(this.carouselContainer.timer);
      e = e || window.event;
      if(e.touches && e.touches[0]) {
        e = e.touches[0];
      }
      let sX = e.clientX,
        sY = e.clientY;

      let onpointermoveEvent = (e)=>{
        this.drag = true;
        e = e || window.event;
        let coef = 1;
        if(e.touches && e.touches[0]) {
          e = e.touches[0];
          coef = 10;
        }
        let nX = e.clientX,
          nY = e.clientY;
        this.desX = nX - sX;
        this.desY = nY - sY;
        this.tX += this.desX * 0.1 * coef;
        this.applyTranform(this.carouselContainer);
        sX = nX;
        sY = nY;
      }
      document.addEventListener('pointermove',onpointermoveEvent)
      document.addEventListener('touchmove', onpointermoveEvent, false);

      let onpointerupEvent=(e)=>{
        this.carouselContainer.timer = setInterval(() => {
          this.desX *= 0.95;
          this.desY *= 0.95;
          this.tX += this.desX * 0.1;
          this.applyTranform(this.carouselContainer);
          this.playSpin(false);
          if (Math.abs(this.desX) < 0.5 && Math.abs(this.desY) < 0.5) {
            clearInterval(this.carouselContainer.timer);
            this.playSpin(true);
          }
        }, 17);

        document.removeEventListener('pointermove',onpointermoveEvent);
        document.removeEventListener('touchmove',onpointermoveEvent);
        document.removeEventListener('pointerup',onpointerupEvent);
        document.removeEventListener('touchend',onpointerupEvent);
      }
      document.addEventListener('pointerup',onpointerupEvent)
      document.addEventListener('touchend',onpointerupEvent)
      return false;
    };

    this.domElement.onpointerdown = onpointerdownEvent;
    this.domElement.ontouchstart = onpointerdownEvent;


    /** Auto Rotate*/
    //this.rotate()
  }

  handleListeners(add) {
    Events[add ? "listen" : "unlisten"]("viewportchanged", this.redraw);
    Events[add ? "listen" : "unlisten"]("splashremoved", this.redraw);
  }

  playLeavingAnimation() {
    this.enterExitCtrl.playLeave();
  }

  cancelLeavingAnimation() {
    this.enterExitCtrl.cancelLeave();
  }

  render(width, height) {
    if (
      !Q3.data.layers.layers2D[this.parentLayer.uuid] ||
      this.disabled ||
      Utils.queryParams.has("disable_2d")
    )
      return;

    if (!this.isMounted || this.target) {
      this.redraw(width, height);
      if (width > 0 && height > 0) {
        this.isMounted = true;
      }
    }
  }

  getRenderPositionAndSize(width, height) {
    const size = RenderManager.getSize();

    if (!width) {
      width = size.width;
    }
    if (!height) {
      height = size.height;
    }

    if (!Q3.editMode && this.inLetterbox !== false) {
      width = Q3.letterbox.offsetWidth / RenderManager.pixelRatio;
      height = Q3.letterbox.offsetHeight / RenderManager.pixelRatio;
    }

    let w = this.width * width * RenderManager.pixelRatio;
    let h = this.height * height * RenderManager.pixelRatio;

    if (w < 40) {
      w = 40;
    }
    if (h < 40) {
      h = 40;
    }

    const [x, y] = this._calcPosition(width, height);

    return { x, y, w, h };
  }

  init() {
    this.domElement.classList[this.hideBackface ? "add" : "remove"]("hide-backface");
    this.aImg = this.spinContainer.querySelectorAll(".img_wrap_carousel");
    this.aEle = this.aImg; // combine 2 arrays

    for (let i = 0; i < this.aEle.length; i++) {
      const transform = "rotateY(" + i * (360 / this.aEle.length) + "deg) translateZ(" + this.radius + "px)";
      const img = this.aEle[i];
      img.classList[this.images[i].isLoading ? "add" : "remove"]("is-loading");
      img.style.transform = transform;
      if (this._separateReflectionElements && img.nextSibling) {
        img.nextSibling.style.transform = transform+' scaleY(-1)';
      }
    }
  }

  calcSize() {
    let aspects = {
      '1x1': 1,
      '16x9': 9 / 16,
      '9x16': 16 / 9,
    }
    let aspect = aspects[this.aspect];
    let w=this.carouselContainer.offsetWidth*0.5;
    let h=this.carouselContainer.offsetHeight * (0.7 + (aspect !== 1 ? aspect > 1 ? -0.2 : 0.3 : 0));
    let min = Math.min(w, h);
    let padding = 0.35;
    let side = Math.min(min, w * (2*Math.sin(Math.PI*2/(this.images.length*2))))*(1-padding);
    this.radius = Math.min(w*0.95, side / (2*Math.sin(Math.PI*2/(this.images.length*2)))/(1-padding));

    return {
      width: side,
      height: side * aspect
    }
  }

  redraw(width, height) {
    !width && (width = RenderManager.domElement.width / RenderManager.pixelRatio);
    !height && (height = RenderManager.domElement.height / RenderManager.pixelRatio);

    let currentSize = this.getVWSize();
    if (this[currentSize]) {
      Object.assign(this, this[currentSize]);
    }

    const size = RenderManager.getSize();

    if (!width) {
      width = size.width;
    }

    if (!height) {
      height = size.height;
    }

    const { x, y, w: elWidth, h: elHeight } = this.getRenderPositionAndSize(width, height);

    this.domElement.style.width = elWidth + "px";
    this.domElement.style.height = elHeight + "px";
    this.domElement.style.left = (x || 0) + "px";
    this.domElement.style.top = (y || 0) + "px";

    //Size of images
    let sizeImg = this.calcSize();

    this.spinContainer.style.width = sizeImg.width+'px';
    this.spinContainer.style.height = sizeImg.height+'px';

    this.init();
  }

  rotate() {
    return false;
    if (this.autoRotate) {
      let animationName = this.rotateSpeed > 0 ? "spin" : "spinRevert";
      this.spinContainer.style.animation = `${animationName} ${Math.abs(
        this.rotateSpeed
      )}s infinite linear`;
    }
  }

  addImage(item) {
    let index = this.images.findIndex((x) => x.empty === true);
    if (index !== -1) {
      this.replaceImage(item, index);
    } else {
      this.images.push(item);
      this.data.images.push(item);
      this.appendImages();
    }
  }

  replaceImage(item, index) {
    this.images[index] = item;
    this.data.images[index] = item;
    this.appendImages();
  }

  updateImages(arr) {
    this.images = arr;
    this.appendImages();
  }

  delete() {
    this.handleListeners(false);

    this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
    this.parentLayer.domElement.removeChild(this.domElement);

    delete this.domElement;

    if (Q3.selected === this) {
      Q3.selected = null;
      Events.fire("selectchange");
    }
  }

  setData(data) {
    let currentSize = this.getVWSize();
    Object.assign(this[currentSize], {
      x: data.x,
      y: data.y,
      width: data.width,
      height: data.height,
    });

    if (
      !Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid][
        currentSize
      ]
    ) {
      Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid][
        currentSize
      ] = {};
    }

    Object.assign(
      Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid][
        currentSize
      ],
      {
        x: data.x,
        y: data.y,
        width: data.width,
        height: data.height,
      }
    );

    this.redraw();
  }
}

export default ImageCarousel;
export { ImageCarousel };
